import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { getVoice } from "@/lib/voices";
import { narrateUrl } from "@/lib/narrate";
import { startNarration, endNarration, stopNarration } from "@/lib/narrator";
import { Sparkles, RefreshCw, Volume2, X, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { TaggedBlocks } from "@/components/TaggedBlocks";

export const AIBriefing = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [audioSrc, setAudioSrc] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [insufficient, setInsufficient] = useState(false);
  const [missingSlots, setMissingSlots] = useState([]);
  const [confidence, setConfidence] = useState(null);
  const [taggedBlocks, setTaggedBlocks] = useState([]);
  const [sources, setSources] = useState([]);
  const [replayId, setReplayId] = useState(null);
  const audioRef = useRef(null);
  // Remembers which (briefing text + voice) the cached audio belongs to, so a
  // repeat "Listen" replays instantly instead of re-synthesising (~25s).
  const audioForRef = useRef(null);

  // Replay-from-ledger hydration: `?briefing=brf_...` in the URL causes
  // this component to fetch and render the *exact* prior briefing —
  // same tagged blocks, same confidence, same sources — instead of
  // showing the Generate button. Ledger Replay chip works end-to-end.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const bid = params.get("briefing");
    if (!bid || bid === replayId) return;
    setReplayId(bid);
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/exec/ai-briefing/${bid}`);
        setBriefing(data.briefing);
        setGeneratedAt(data.generated_at);
        setInsufficient(!!data.insufficient_data);
        setMissingSlots(data.missing_slots || []);
        setConfidence(typeof data.confidence === "number" ? data.confidence : null);
        setTaggedBlocks(Array.isArray(data.tagged_blocks) ? data.tagged_blocks : []);
        setSources(Array.isArray(data.sources) ? data.sources : []);
      } catch {
        toast.error("That briefing is no longer available.");
      } finally {
        setLoading(false);
      }
    })();
  }, [location.search, replayId]);

  const generate = async () => {
    setLoading(true);
    setAudioSrc(null);
    audioForRef.current = null;
    try {
      const { data } = await api.get("/exec/ai-briefing");
      setBriefing(data.briefing);
      setGeneratedAt(data.generated_at);
      setInsufficient(!!data.insufficient_data);
      setMissingSlots(data.missing_slots || []);
      setConfidence(typeof data.confidence === "number" ? data.confidence : null);
      setTaggedBlocks(Array.isArray(data.tagged_blocks) ? data.tagged_blocks : []);
      setSources(Array.isArray(data.sources) ? data.sources : []);
    } catch {
      toast.error("AI briefing unavailable");
    } finally {
      setLoading(false);
    }
  };

  const listen = async () => {
    // Refuse to synthesise audio for an insufficient-data refusal —
    // playing "we don't have enough evidence" over a boardroom PA
    // system is theatre we don't want.
    if (insufficient) {
      toast.info("No audio for an insufficient-evidence briefing.");
      return;
    }
    const voice = getVoice();
    // Cached audio for this exact briefing + voice → replay instantly.
    if (audioSrc && audioForRef.current === `${briefing}::${voice}`) {
      audioRef.current?.play().catch(() => {});
      return;
    }
    setLoadingAudio(true);
    try {
      // Ensure we have the briefing text (generate it once if needed).
      let text = briefing;
      if (!text) {
        const { data } = await api.get("/exec/ai-briefing");
        text = data.briefing;
        setBriefing(text);
        setGeneratedAt(data.generated_at);
        setInsufficient(!!data.insufficient_data);
        setMissingSlots(data.missing_slots || []);
        setConfidence(typeof data.confidence === "number" ? data.confidence : null);
        setTaggedBlocks(Array.isArray(data.tagged_blocks) ? data.tagged_blocks : []);
        setSources(Array.isArray(data.sources) ? data.sources : []);
        if (data.insufficient_data) {
          toast.info("Insufficient evidence — audio synthesis skipped.");
          setLoadingAudio(false);
          return;
        }
      }
      const src = await narrateUrl(text, voice);
      setAudioSrc(src);
      audioForRef.current = `${text}::${voice}`;
      setTimeout(() => audioRef.current?.play().catch(() => {}), 100);
    } catch {
      toast.error("Audio briefing unavailable");
    } finally {
      setLoadingAudio(false);
    }
  };

  const close = () => {
    if (audioRef.current) audioRef.current.pause();
    stopNarration();
    setBriefing(null);
    setAudioSrc(null);
    setGeneratedAt(null);
    setInsufficient(false);
    setMissingSlots([]);
    setConfidence(null);
    setTaggedBlocks([]);
    setSources([]);
    setReplayId(null);
    // If we arrived here from a Replay deep-link, clear the query
    // param so a subsequent Generate doesn't re-hydrate the old one.
    if (location.search.includes("briefing=")) {
      navigate(location.pathname, { replace: true });
    }
    audioForRef.current = null;
  };

  return (
    <section className="mt-12" data-testid="ai-briefing-section">
      <div className="relative border border-ek-purple/15 bg-gradient-to-br from-ek-purple/[0.04] to-transparent p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="ek-eyebrow flex items-center gap-1">
              <Sparkles size={12} /> AI executive briefing
            </h2>
            <p className="mt-1 text-sm text-ek-ink/65">
              A board-ready narrative auto-written from the live risk picture — read or listen
              (spoken in your chosen Ask&nbsp;ERIO voice).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={listen}
              disabled={loadingAudio || loading}
              data-testid="listen-briefing-button"
              className="ek-btn-outline !py-2 text-sm disabled:opacity-50"
            >
              {loadingAudio ? <RefreshCw size={15} className="animate-spin" /> : <Volume2 size={15} />}
              {loadingAudio ? "Generating audio…" : "Listen"}
            </button>
            <button
              onClick={generate}
              disabled={loading}
              data-testid="generate-briefing-button"
              className="ek-btn !py-2 text-sm disabled:opacity-50"
            >
              {loading ? <RefreshCw size={15} className="animate-spin" /> : <Sparkles size={15} />}
              {loading ? "Generating…" : briefing ? "Regenerate" : "Generate briefing"}
            </button>
            {(briefing || audioSrc) && (
              <button
                onClick={close}
                data-testid="close-briefing-button"
                title="Close briefing"
                aria-label="Close briefing"
                className="rounded-full border border-black/10 p-2 text-ek-ink/65 transition-colors hover:border-ek-ink/30 hover:text-ek-ink"
              >
                <X size={15} />
              </button>
            )}
          </div>
        </div>

        {audioSrc && (
          <audio
            ref={audioRef}
            controls
            src={audioSrc}
            data-testid="briefing-audio-player"
            onPlay={() => startNarration(audioRef.current, () => audioRef.current?.pause())}
            onEnded={() => endNarration(audioRef.current)}
            className="mt-4 w-full"
          />
        )}

        {briefing && insufficient && (
          <div
            className="mt-5 rounded-lg border border-amber-300/70 bg-amber-50/50 p-4"
            data-testid="ai-briefing-insufficient"
          >
            <div className="flex items-start gap-2">
              <AlertCircle size={16} className="mt-0.5 shrink-0 text-amber-600" />
              <div className="min-w-0 flex-1">
                <p className="font-display text-sm font-semibold text-amber-900">
                  Insufficient evidence for a board recommendation
                </p>
                <p className="mt-1 text-xs text-amber-900/80">
                  ERIO refused to fabricate — grounded evidence is too thin
                  {confidence !== null && (
                    <> (confidence {(confidence * 100).toFixed(0)}%)</>
                  )}. Populate the missing slots below and try again.
                </p>
                {missingSlots.length > 0 && (
                  <ul
                    className="mt-3 space-y-2 text-sm text-ek-ink/80"
                    data-testid="ai-briefing-missing-slots"
                  >
                    {missingSlots.map((gap) => (
                      <li
                        key={gap.slot}
                        className="rounded-md border border-amber-200 bg-white p-2.5"
                        data-testid={`ai-briefing-slot-${gap.slot}`}
                      >
                        <p className="text-xs font-semibold text-ek-ink">{gap.label}</p>
                        <p className="mt-0.5 text-xs text-ek-ink/65">{gap.action}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {briefing && !insufficient && (
          <div className="mt-5" data-testid="ai-briefing-text">
            <TaggedBlocks
              blocks={taggedBlocks}
              fallbackText={briefing}
              testIdPrefix="ai-briefing"
            />
            {sources.length > 0 && (
              <div
                className="mt-4 flex flex-wrap items-center gap-1.5 border-t border-black/5 pt-3"
                data-testid="ai-briefing-sources"
              >
                <span className="text-[10px] font-semibold uppercase tracking-wide text-ek-ink/50">
                  Grounded in
                </span>
                {sources.slice(0, 8).map((s) => (
                  <Link
                    key={s.id}
                    to={`/explore/risks/${s.id}/impact`}
                    className="inline-flex items-center gap-1 rounded-full border border-ek-purple/20 bg-ek-purple/5 px-2 py-0.5 text-[11px] font-medium text-ek-purple transition-colors hover:bg-ek-purple/10"
                    title={s.theme ? `${s.title} — ${s.theme}` : s.title}
                    data-testid={`ai-briefing-source-${s.id}`}
                  >
                    {s.title.length > 40 ? `${s.title.slice(0, 40)}…` : s.title}
                  </Link>
                ))}
                {sources.length > 8 && (
                  <span className="text-[11px] text-ek-ink/45" data-testid="ai-briefing-sources-more">
                    +{sources.length - 8} more
                  </span>
                )}
              </div>
            )}
            <div className="mt-3 flex items-center justify-between gap-3">
              {generatedAt && (
                <p className="text-[11px] text-ek-ink/35">
                  Generated {new Date(generatedAt).toLocaleString()} · AI-assisted, review before distribution.
                  {confidence !== null && (
                    <> · Confidence {(confidence * 100).toFixed(0)}%</>
                  )}
                </p>
              )}
              <button
                onClick={close}
                data-testid="dismiss-briefing-link"
                className="shrink-0 text-[11px] font-semibold text-ek-ink/65 transition-colors hover:text-ek-ink"
              >
                Close briefing
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
